// actionDefinitions.ts — quick-create / navigate action commands for the palette.
//
// Each entry specifies a route target and a persona gate that mirrors the
// RequireRole / visible() guard on the destination page.  Do not widen gates
// here without widening the matching nav/route gate.

import type { Persona } from '@/hooks/usePersona'

export interface ActionDef {
  id: string
  label: string
  /** Canonical route to navigate to. Verified against App.tsx routes. */
  path: string
  /** Returns true when this action should be visible for the given persona. */
  gate: (p: Persona) => boolean
}

/**
 * Quick-create actions shown in the Actions group.
 *
 * Target route verification (App.tsx):
 *   /tenant/build/workflows/new      — BuildHub child, line ~555
 *   /tenant/build/agent-graphs/new   — BuildHub child, line ~562
 *   /tenant/build/runbooks/new       — BuildHub child, line ~587
 *   /tenant/build/teams              — BuildHub child, line ~623 (no create-only route)
 *   /tenant/monitor                  — Monitor hub, AutomationsPage (W11.2; was /tenant/automations)
 *   /tenant/cost/reports             — standalone ReportsPage, line ~714
 *   /tenant/admin/users              — AdminHub child, line ~814
 */
export const ACTION_DEFS: ActionDef[] = [
  {
    id: 'new-workflow',
    label: 'New Workflow',
    path: '/tenant/build/workflows/new',
    // Two conditions, not one. `p.isTenant` is the SCOPE guard — it keeps
    // every tenant action out of a platform persona's palette, which
    // CommandPalette.test.tsx pins ("hides all tenant actions when isTenant
    // is false"); dropping it leaks this action to platform_admin, who holds
    // all permissions. `canCreateWorkflow` is the PERMISSION: this opens the
    // CREATE form, needing create:workflow (tenant_owner/tenant_admin/
    // tenant_operator), where the tab gate alone offered it to six roles whose
    // submit the server refuses. The palette was the one create entry point
    // the in-page canCreateWorkflow gates did not cover.
    gate: (p) => p.isTenant && p.canCreateWorkflow,
  },
  {
    id: 'new-agent-graph',
    label: 'New Agent Graph',
    path: '/tenant/build/agent-graphs/new',
    // Same shape as new-workflow: scope guard AND permission. This opens a
    // CREATE page, so it follows create:stategraph (tenant_owner/tenant_admin/
    // tenant_operator per role_matrix.golden) rather than the tab's view gate.
    // canOperateTenantResources is that set plus platform_admin, which is why
    // `p.isTenant` must stay in front of it.
    gate: (p) => p.isTenant && p.canOperateTenantResources,
  },
  {
    id: 'new-runbook',
    label: 'New Runbook',
    path: '/tenant/build/runbooks/new',
    // CreateRunbook is gated IN-HANDLER, not by route middleware, so neither
    // the golden nor a routes.go grep shows it: internal/api/v1/runbook/
    // handler.go refuses anyone who is not platform_admin / tenant_owner /
    // tenant_admin with "creating a runbook requires admin or owner". This
    // entry was p.isTenant, so all nine tenant roles were offered a create
    // page whose submit they cannot use. canManageTenantResources is that
    // exact set; `p.isTenant` stays in front of it as the scope guard.
    //
    // NOTE the AI draft route is deliberately WIDER (DraftRunbook also admits
    // tenant_operator), which is why the /runbooks/new ROUTE is guarded on the
    // operator set rather than this one — see App.tsx.
    gate: (p) => p.isTenant && p.canManageTenantResources,
  },
  {
    id: 'new-team',
    label: 'New Team',
    path: '/tenant/build/teams',
    // Mirrors Build › Teams tab: visible: (p) => p.isTenant
    gate: (p) => p.isTenant,
  },
  {
    id: 'new-schedule',
    // Nit1: no schedule-create route exists — label matches the destination.
    label: 'View Schedules',
    // No schedule-create route exists; lands on the Monitor hub's Schedules
    // tab (W11.2 — was the Runs hub / AutomationsPage at /tenant/automations).
    path: '/tenant/monitor?tab=schedules',
    // Mirrors Monitor hub: visible: (p) => p.isTenant
    gate: (p) => p.isTenant,
  },
  {
    id: 'generate-report',
    label: 'Generate Report',
    path: '/tenant/cost/reports',
    // M1: mirrors the /tenant/cost/reports RequireRole page gate, NOT the looser
    // Cost-hub canViewCost (operators hold COST_READ but would hit the RequireRole
    // wall). That gate is now owner/admin/platform_admin/tenant_billing_admin;
    // canManageUsers is owner+admin, so billing_admin needs the explicit arm or
    // this entry stays hidden from a role the route now admits. canManageBudget is
    // still wrong: it also includes tenant_cost_center_admin, which the route denies.
    // Persona exposes no capability set, so this is a role check by necessity —
    // keep it in step with App.tsx's RequireRole for this path.
    gate: (p) => p.isTenant && (p.canManageUsers || p.role === 'tenant_billing_admin'),
  },
  {
    id: 'invite-user',
    label: 'Invite User',
    path: '/tenant/admin/users',
    // Mirrors Admin › Users tab: visible: (p) => p.isTenant && p.canManageUsers
    gate: (p) => p.isTenant && p.canManageUsers,
  },
]

/**
 * Extra search aliases appended to hub-level nav items in buildNavCommands.
 * Keyed by NavItem.name so old names (e.g. "executions") find the new hub.
 * Not in navigation.ts because these are palette-search concerns only.
 */
export const HUB_SEARCH_ALIASES: Record<string, string[]> = {
  // 'airflow' and 'dagster' here let typing the engine name find the Monitor
  // hub (engine-filter intent). The console intent is served by
  // EXTRA_NAV_COMMANDS below. 'runs'/'automations' kept as aliases — W11.2
  // renamed the hub from "Runs" (route /tenant/automations) to "Monitor"
  // (route /tenant/monitor), but the old name is still muscle memory.
  Monitor: [
    'executions', 'execution history', 'run history', 'schedules', 'loops', 'automations', 'airflow', 'dagster',
    'runs', 'today', 'run plan',
  ],
  Governance: ['compliance', 'policy'],
  Admin: ['administration', 'organization'],
  Build: ['create', 'builder'],
  AI: ['llm', 'models', 'ai hub'],
  // Deleted's hub-page aliases moved here from the old per-type HubTab entries
  // (WS: unified-recycle-bin) — the hub now has tabs:[] since it's a single
  // unified page, so these terms would otherwise be lost from the palette.
  Deleted: ['recycle bin', 'trash', 'restore', 'deleted workflow', 'deleted agent', 'deleted prompt', 'deleted cluster', 'deleted connection', 'permanent delete', 'purge'],
}

/**
 * Additional navigation commands that are not derived from hubDefinitions.
 * These are pages that live outside the 8-hub nav (console pages, utility pages)
 * but should still be searchable from the palette.
 *
 * All paths are /tenant/ routes — CommandPalette filters these for isTenant.
 */
export interface ExtraNavCommand {
  id: string
  label: string
  /** Space-joined search terms (label + aliases). */
  value: string
  path: string
}

export const EXTRA_NAV_COMMANDS: ExtraNavCommand[] = [
  {
    id: 'airflow-console',
    label: 'Airflow Console',
    // aliases: airflow, dags, dag editor, console — distinct label from "Go to Runs ?engine=airflow"
    value: 'Airflow Console airflow dags dag editor console',
    path: '/tenant/data/airflow',
  },
  {
    id: 'dagster-console',
    label: 'Dagster Console',
    // aliases: dagster, assets, data pipeline, console
    value: 'Dagster Console dagster assets data pipeline console',
    path: '/tenant/data/dagster',
  },
  {
    id: 'favorites',
    label: 'Favorites',
    value: 'Favorites favorites starred bookmarks',
    path: '/tenant/favorites',
  },
  {
    // R9: /tenant/search has no palette entry; header search bar is hidden below
    // the lg breakpoint so the palette is the only keyboard path at small sizes.
    id: 'search',
    label: 'Search',
    value: 'Search find search',
    path: '/tenant/search',
  },
]
